import React from 'react';
import proptypes from 'prop-types';
import {
  FormController,
  FormLabel,
  Dropdown,
  DropdownField,
  MenuItem,
  TextField
} from '@one-thd/sui-atomic-components';
import { DatePicker } from '@one-thd/sui-date-pickers';
import { ValidatedTextField, ValidatedDropdown } from './ValidatedFields';

const {
  arrayOf, bool, func, shape, string
} = proptypes;

// Section components for ProjectInformationForm.
//
// ProjectInformationForm renders the same set of fields twice - once for its
// collapsible-accordion layout and once for its always-visible layout - which is
// why its renderFormFields carried a Cognitive Complexity of 156 (Sonar S3776).
// The two layouts differ only in field order and a couple of wrapper elements, so
// each field is defined once here and both layouts compose the same pieces.
//
// Fields that are a plain text input or a plain dropdown are not re-wrapped here;
// the form uses ValidatedTextField / ValidatedDropdown directly. Only the four
// groupings below carry structure beyond a single field and so earn a component.
//
// Every `hidden` prop mirrors a `{!hideProjectInfo && ...}` / `{!hideDeliveryInfo
// && ...}` guard that used to sit at the call site. Keeping the guard in here is
// what lets the form's field list stay flat and free of conditionals.

// Project Type: a preset dropdown that reveals a free-text field when "Other" is
// picked. Deliberately NOT built on ValidatedDropdown - this dropdown shows no
// validation status of its own, its test id is unsuffixed (unlike every other
// dropdown in the form), and it caps its menu at four visible items.
export const ProjectTypeSection = ({
  hidden,
  idSuffix,
  dropdownValue,
  onDropdownChange,
  onDropdownBlur,
  projectType,
  projectTypeRef,
  onProjectTypeChange,
  onProjectTypeBlur,
  error,
  showErrors
}) => {
  if (hidden) {
    return null;
  }
  return (
    <FormController fullWidth>
      <FormLabel
        optional
        htmlFor={`project-type-form-dropdown${idSuffix}`}
        id={`project-type-form-label${idSuffix}`}
      >
        Project Type
      </FormLabel>
      <Dropdown
        maxItems={4}
        id={`project-type-form-dropdown${idSuffix}`}
        data-testid="project-type-dropdown"
        value={dropdownValue ?? null}
        onBlur={onDropdownBlur}
        onChange={onDropdownChange}
        placeholder="Select a Project Type"
      >
        <MenuItem value="" data-testid="project-type-none">None</MenuItem>
        <MenuItem value="Single Family" data-testid="single-family">Single Family</MenuItem>
        <MenuItem value="Multi-Family" data-testid="multi-family">Multi-Family</MenuItem>
        <MenuItem value="Renovation/Remodel" data-testid="renovation-remodel">Renovation/Remodel</MenuItem>
        <MenuItem value="Other" data-testid="type-other">Other</MenuItem>
      </Dropdown>
      {dropdownValue === 'Other' && (
        <TextField
          fullWidth
          ref={projectTypeRef}
          id="project-type"
          placeholder="Project Type"
          value={projectType || ''}
          onBlur={onProjectTypeBlur}
          onChange={onProjectTypeChange}
          status={showErrors && error ? 'error' : null}
          statusMessage={showErrors ? error : ''}
          InputProps={{
            inputProps: {
              'data-testid': 'project-type-input'
            }
          }}
        />
      )}
    </FormController>
  );
};

ProjectTypeSection.displayName = 'ProjectTypeSection';

ProjectTypeSection.propTypes = {
  onDropdownChange: func.isRequired,
  onDropdownBlur: func.isRequired,
  onProjectTypeChange: func.isRequired,
  onProjectTypeBlur: func.isRequired,
  hidden: bool,
  idSuffix: string,
  dropdownValue: string,
  projectType: string,
  projectTypeRef: shape({}),
  error: string,
  showErrors: bool
};

ProjectTypeSection.defaultProps = {
  hidden: false,
  idSuffix: '',
  dropdownValue: null,
  projectType: '',
  projectTypeRef: null,
  error: '',
  showErrors: false
};

// The two estimated-date pickers. The only difference between the form's two
// layouts is how these are wrapped: one grid for the pair, versus a bottom margin
// on each. sui- class names are kept as whole static strings per the stack rules.
export const EstimatedDatesSection = ({
  hidden,
  isAccordion,
  estimatedStartDate,
  estimatedEndDate,
  estimatedEndDateRef,
  onStartDateChange,
  onEndDateChange,
  endDateError,
  showErrors
}) => {
  if (hidden) {
    return null;
  }

  const startDatePicker = (
    <DatePicker
      dateFieldProps={{
        label: 'Estimated Start Date',
        optional: 'true'
      }}
      collapsible
      value={estimatedStartDate || ''}
      onChange={onStartDateChange}
      id="project-start-date"
      data-testid="project-start-date-input"
    />
  );

  const endDatePicker = (
    <DatePicker
      ref={estimatedEndDateRef}
      dateFieldProps={{
        label: 'Estimated End Date',
        optional: 'true'
      }}
      collapsible
      value={estimatedEndDate || ''}
      onChange={onEndDateChange}
      id="project-end-date"
      data-testid="project-end-date-input"
      status={showErrors && endDateError ? 'error' : null}
      statusMessage={showErrors ? endDateError : ''}
    />
  );

  if (isAccordion) {
    return (
      <div className="sui-grid sui-gap-2">
        {startDatePicker}
        {endDatePicker}
      </div>
    );
  }

  return (
    <div>
      <div className="sui-mb-6">
        {startDatePicker}
      </div>
      <div className="sui-mb-6">
        {endDatePicker}
      </div>
    </div>
  );
};

EstimatedDatesSection.displayName = 'EstimatedDatesSection';

EstimatedDatesSection.propTypes = {
  onStartDateChange: func.isRequired,
  onEndDateChange: func.isRequired,
  hidden: bool,
  isAccordion: bool,
  estimatedStartDate: string,
  estimatedEndDate: string,
  estimatedEndDateRef: shape({}),
  endDateError: string,
  showErrors: bool
};

EstimatedDatesSection.defaultProps = {
  hidden: false,
  isAccordion: false,
  estimatedStartDate: '',
  estimatedEndDate: '',
  estimatedEndDateRef: null,
  endDateError: '',
  showErrors: false
};

// Property Type plus the "describe it" field that appears for any value outside the
// preset list. Both halves are ordinary validated fields, so they reuse the shared
// wrappers; this component exists to keep the pair's reveal logic out of the form.
export const PropertyTypeSection = ({
  hidden,
  idSuffix,
  testIdSuffix,
  propertyType,
  isPropertyTypeOther,
  propertyTypeOther,
  propertyTypeRef,
  propertyTypeOtherRef,
  onPropertyTypeChange,
  onPropertyTypeBlur,
  onPropertyTypeOtherChange,
  onPropertyTypeOtherBlur,
  propertyTypeError,
  propertyTypeOtherError,
  showErrors
}) => {
  if (hidden) {
    return null;
  }
  return (
    <>
      <ValidatedDropdown
        ref={propertyTypeRef}
        id={`property-type${idSuffix}`}
        testId={`property-type${testIdSuffix}-dropdown`}
        label="Property Type"
        value={isPropertyTypeOther ? 'Other' : (propertyType ?? null)}
        onBlur={onPropertyTypeBlur}
        onChange={onPropertyTypeChange}
        placeholder="Select a property type"
        error={propertyTypeError}
        showErrors={showErrors}
      >
        <MenuItem value="" data-testid={`property-type${testIdSuffix}-none`}>None</MenuItem>
        <MenuItem value="House" data-testid={`property-type${testIdSuffix}-house`}>House</MenuItem>
        <MenuItem value="Apartment" data-testid={`property-type${testIdSuffix}-apartment`}>Apartment</MenuItem>
        <MenuItem value="Business" data-testid={`property-type${testIdSuffix}-business`}>Business</MenuItem>
        <MenuItem value="Construction Site" data-testid={`property-type${testIdSuffix}-construction-site`}>
          Construction Site
        </MenuItem>
        <MenuItem value="Other" data-testid={`property-type${testIdSuffix}-other`}>Other</MenuItem>
      </ValidatedDropdown>

      <ValidatedTextField
        hidden={!isPropertyTypeOther}
        ref={propertyTypeOtherRef}
        required
        id="property-type-other"
        label="Describe property type"
        value={propertyTypeOther}
        onBlur={onPropertyTypeOtherBlur}
        onChange={onPropertyTypeOtherChange}
        error={propertyTypeOtherError}
        showErrors={showErrors}
        testId="property-type-other-input"
      />
    </>
  );
};

PropertyTypeSection.displayName = 'PropertyTypeSection';

PropertyTypeSection.propTypes = {
  onPropertyTypeChange: func.isRequired,
  onPropertyTypeBlur: func.isRequired,
  onPropertyTypeOtherChange: func.isRequired,
  onPropertyTypeOtherBlur: func.isRequired,
  hidden: bool,
  idSuffix: string,
  testIdSuffix: string,
  propertyType: string,
  isPropertyTypeOther: bool,
  propertyTypeOther: string,
  propertyTypeRef: shape({}),
  propertyTypeOtherRef: shape({}),
  propertyTypeError: string,
  propertyTypeOtherError: string,
  showErrors: bool
};

PropertyTypeSection.defaultProps = {
  hidden: false,
  idSuffix: '',
  testIdSuffix: '',
  propertyType: null,
  isPropertyTypeOther: false,
  propertyTypeOther: '',
  propertyTypeRef: null,
  propertyTypeOtherRef: null,
  propertyTypeError: '',
  propertyTypeOtherError: '',
  showErrors: false
};

// Job Site Hours start/end. These are DropdownField (not Dropdown) and, unlike every
// other field in the form, their error state is NOT gated on showErrors - the window
// and missing-end errors surface as soon as they are set. End stays disabled until a
// start time exists, and its option list is narrowed to keep a six-hour minimum.
export const JobSiteHoursSection = ({
  hidden,
  idSuffix,
  testIdSuffix,
  jobSiteHoursStart,
  jobSiteHoursEnd,
  startTimeOptions,
  endTimeOptions,
  jobSiteHoursStartRef,
  jobSiteHoursEndRef,
  onStartChange,
  onStartBlur,
  onEndChange,
  onEndBlur,
  startError,
  endError
}) => {
  if (hidden) {
    return null;
  }
  const labelProps = { optional: !jobSiteHoursStart, required: !!jobSiteHoursStart };
  return (
    <>
      <DropdownField
        fullWidth
        label="Job Site Hours Start"
        labelProps={labelProps}
        helpMessage="Job site hours must be a minimum of 6 hours"
        id={`job-site-hours-start${idSuffix}`}
        value={jobSiteHoursStart ?? null}
        onChange={onStartChange}
        status={startError ? 'error' : null}
        statusMessage={startError || ''}
        dropdownProps={{
          ref: jobSiteHoursStartRef,
          'data-testid': `job-site-hours-start${testIdSuffix}-dropdown`,
          onBlur: onStartBlur,
          placeholder: 'Select start time'
        }}
      >
        <MenuItem value="" data-testid={`start-time${testIdSuffix}-none`}>None</MenuItem>
        {startTimeOptions.map((time) => (
          <MenuItem
            key={time}
            value={time}
            data-testid={`start-time${testIdSuffix}-${time.replace(/[:\s]/g, '-')}`}
          >
            {time}
          </MenuItem>
        ))}
      </DropdownField>

      <DropdownField
        fullWidth
        label="Job Site Hours End"
        labelProps={labelProps}
        id={`job-site-hours-end${idSuffix}`}
        disabled={!jobSiteHoursStart}
        value={jobSiteHoursEnd || ''}
        onChange={onEndChange}
        status={endError ? 'error' : null}
        statusMessage={endError || ''}
        dropdownProps={{
          ref: jobSiteHoursEndRef,
          'data-testid': `job-site-hours-end${testIdSuffix}-dropdown`,
          onBlur: onEndBlur,
          placeholder: 'Select end time'
        }}
      >
        {endTimeOptions.map((time) => (
          <MenuItem
            key={time}
            value={time}
            data-testid={`end-time${testIdSuffix}-${time.replace(/[:\s]/g, '-')}`}
          >
            {time}
          </MenuItem>
        ))}
      </DropdownField>
    </>
  );
};

JobSiteHoursSection.displayName = 'JobSiteHoursSection';

JobSiteHoursSection.propTypes = {
  onStartChange: func.isRequired,
  onStartBlur: func.isRequired,
  onEndChange: func.isRequired,
  onEndBlur: func.isRequired,
  startTimeOptions: arrayOf(string).isRequired,
  endTimeOptions: arrayOf(string).isRequired,
  hidden: bool,
  idSuffix: string,
  testIdSuffix: string,
  jobSiteHoursStart: string,
  jobSiteHoursEnd: string,
  jobSiteHoursStartRef: shape({}),
  jobSiteHoursEndRef: shape({}),
  startError: string,
  endError: string
};

JobSiteHoursSection.defaultProps = {
  hidden: false,
  idSuffix: '',
  testIdSuffix: '',
  jobSiteHoursStart: null,
  jobSiteHoursEnd: '',
  jobSiteHoursStartRef: null,
  jobSiteHoursEndRef: null,
  startError: '',
  endError: ''
};
